import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import cx from 'classnames';

import type {AriaMenuItem} from '@pexip/components';
import {Button, Icon, IconTypes, Tooltip} from '@pexip/components';

import {ParticipantRowMoreActions} from '../ParticipantRowMoreActions/ParticipantRowMoreActions.view';
import type {InMeetingParticipant, ParticipantID} from '../../types';
import {SpeakingIndicator} from '../SpeakingIndicator/SpeakingIndicator.view';
import {TestId} from '../../../test/testIds';

import type {RowActionState} from './ParticipantRowActions.module';
import {
    getShouldShowHostActions,
    getShouldShowIndicators,
} from './ParticipantRowActions.module';

import styles from './ParticipantRowActions.module.scss';
import {IndicatorIcon} from './IndicatorIcon';

export const ParticipantRowActions: React.FC<
    Omit<RowActionState, 'isMuted' | 'isCameraMuted' | 'isPresenting'> & {
        isTouchDevice: boolean;
        isDirectMedia?: boolean;
        showParticipantAuthenticatedState: boolean;
        displayLowerHandOnly?: boolean;
        displayName: string;
        handleRemoveUserRequest: (
            participant: InMeetingParticipant,
            displayName: string,
        ) => void;
        handleTransfer?: (identity: ParticipantID) => void;
        handleDTMF?: (identity: ParticipantID) => void;
        handleFecc?: (identity: ParticipantID) => void;
        handleLowerHand?: (identity: ParticipantID) => void;
        isApiParticipant?: boolean;
        isCurrentPeer: boolean;
        isHost: boolean;
        participant: InMeetingParticipant;
        onMouseLeave: () => void;
        isSpotlightAvailable?: boolean;
        additionalMenuContent?: AriaMenuItem[];
    }
> = ({
    isTouchDevice,
    isDirectMedia,
    showParticipantAuthenticatedState,
    displayName,
    displayLowerHandOnly = false,
    handleRemoveUserRequest,
    handleTransfer,
    handleDTMF,
    handleFecc,
    handleLowerHand,
    isApiParticipant,
    isCurrentPeer,
    canPerformHostActions,
    isHost,
    isHover,
    isSpeaking,
    participant,
    onMouseLeave,
    isSpotlightAvailable,
    additionalMenuContent = [],
}) => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const {isMuted, isClientMuted, isCameraMuted, isPresenting, isSpotlight} =
        participant;

    const lowerHandContent = (
        <Tooltip text={t('common.lower-hand', 'Lower hand')} position="top">
            <Button
                data-testid={TestId.ButtonLowerHandParticipantRow}
                aria-label={t('common.lower-hand', 'Lower hand')}
                role="button"
                variant="transparent"
                modifier="square"
                size="compact"
                onClick={() => handleLowerHand?.(participant.uuid)}
            >
                <Icon
                    data-testid={TestId.ActionLowerHand}
                    source={IconTypes.IconLowerhand}
                    size="compact"
                />
            </Button>
        </Tooltip>
    );

    if (handleLowerHand && displayLowerHandOnly && canPerformHostActions) {
        return (
            <div className={styles.moreMenuAndIndicatorsContainer}>
                {lowerHandContent}
            </div>
        );
    }

    const isInteracted = isHover || isOpen;

    const shouldShowIndicators = getShouldShowIndicators({
        canPerformHostActions,
        isInteracted,
    });
    const shouldShowHostActions = getShouldShowHostActions({
        canPerformHostActions,
        isCurrentPeer,
        isHost,
        isInteracted,
    });
    const shouldShowHostActionsForCurrentPeer = getShouldShowHostActions({
        canPerformHostActions,
        isCurrentPeer: false,
        isHost,
        isInteracted,
    });
    const shouldShowActionOrIndicator =
        shouldShowIndicators || shouldShowHostActions;

    const hasAdditionalActions =
        participant.spotlight ??
        participant.setRole ??
        participant.lowerHand ??
        handleTransfer ??
        handleDTMF ??
        handleFecc;
    // Rendered regardless of interaction, so the menu button stays reachable by
    // keyboard without making the whole row a tab stop.
    const shouldRenderAdditionalActions =
        additionalMenuContent.length > 0 ||
        (isHost &&
            canPerformHostActions &&
            hasAdditionalActions &&
            (participant.canSpotlight ||
                participant.canTransfer ||
                participant.canDisconnect));
    const shouldShowAdditionalActions =
        shouldRenderAdditionalActions && isInteracted;

    const menuButton = (
        <Button
            aria-label={
                displayName
                    ? t('media.open-menu-for', {
                          defaultValue: 'Open menu for {{displayName}}',
                          displayName,
                      })
                    : t('common.open-menu', 'Open menu')
            }
            role="button"
            className={cx(styles.indicator, {
                [styles.visuallyHidden]: !shouldShowAdditionalActions,
            })}
            isActive={isOpen}
            variant="secondary"
            modifier="square"
            size="compact"
            onClick={() => setIsOpen(!isOpen)}
            data-testid={TestId.ParticipantMenuButton}
        >
            <Icon
                size="small"
                className={styles.actionButtonIcon}
                source={IconTypes.IconMoreHorizontal}
            />
        </Button>
    );

    const mutedText = isMuted
        ? t('media.muted-by-a-host', 'Muted by a host')
        : t('media.muted', 'Muted');
    const cameraTurnedOffText = t(
        'media.camera-turned-off',
        'Camera turned off',
    );
    const spotlightText = t('media.spotlight', 'Spotlight');
    const presentingText = t('media.presentation.presenting', 'Presenting');

    return (
        <div className={styles.moreMenuAndIndicatorsContainer}>
            {shouldRenderAdditionalActions && (
                <ParticipantRowMoreActions
                    isOpen={isOpen}
                    isTouchDevice={isTouchDevice}
                    showParticipantAuthenticatedState={
                        showParticipantAuthenticatedState
                    }
                    isMuted={isMuted}
                    displayName={displayName}
                    handleTransfer={handleTransfer}
                    handleDTMF={handleDTMF}
                    handleFecc={handleFecc}
                    handleRemoveUserRequest={handleRemoveUserRequest}
                    shouldShowHostActions={shouldShowHostActions}
                    shouldShowHostActionsForCurrentPeer={
                        shouldShowHostActionsForCurrentPeer
                    }
                    isApiParticipant={isApiParticipant}
                    isDirectMedia={isDirectMedia}
                    isCurrentPeer={isCurrentPeer}
                    participant={participant}
                    isSpotlight={isSpotlight}
                    isSpotlightAvailable={isSpotlightAvailable}
                    additionalMenuContent={additionalMenuContent}
                    button={menuButton}
                    onOpenChange={state => {
                        setIsOpen(state);
                        if (!state) {
                            onMouseLeave();
                        }
                    }}
                />
            )}
            {isPresenting && shouldShowActionOrIndicator && (
                <Tooltip text={presentingText} position="top">
                    <IndicatorIcon
                        aria-hidden="false"
                        aria-label={presentingText}
                        data-testid={TestId.IconPresenting}
                        source={IconTypes.IconPresentationOn}
                    />
                </Tooltip>
            )}
            {handleLowerHand &&
                !displayLowerHandOnly &&
                canPerformHostActions &&
                lowerHandContent}
            {isCameraMuted && (
                <Tooltip text={cameraTurnedOffText} position="top">
                    <IndicatorIcon
                        aria-hidden="false"
                        aria-label={cameraTurnedOffText}
                        data-testid={TestId.IconCameraMuted}
                        source={IconTypes.IconVideoOff}
                    />
                </Tooltip>
            )}
            {(isMuted || isClientMuted) && (
                <Tooltip text={mutedText} position="top">
                    <IndicatorIcon
                        aria-hidden="false"
                        aria-label={mutedText}
                        data-testid={
                            isMuted ? TestId.IconMutedByAHost : TestId.IconMuted
                        }
                        source={
                            isMuted
                                ? IconTypes.IconMicrophoneMutedByHost
                                : IconTypes.IconMicrophoneOff
                        }
                    />
                </Tooltip>
            )}
            {isSpotlight && (
                <Tooltip text={spotlightText} position="top">
                    <IndicatorIcon
                        aria-hidden="false"
                        aria-label={spotlightText}
                        data-testid={TestId.IconSpotlight}
                        source={IconTypes.IconSpotlight}
                    />
                </Tooltip>
            )}
            {isSpeaking && !isMuted && shouldShowIndicators && (
                <SpeakingIndicator data-testid={TestId.SpeakingIndicator} />
            )}
        </div>
    );
};

export type ParticipantRowActionsProps = React.ComponentProps<
    typeof ParticipantRowActions
>;
